/**
 * Alle Geräte der Plattform — für den Admin (#89).
 *
 * **Läuft die Flotte?** Oben je Zustand eine Zahl, die zugleich filtert; darunter
 * jedes Gerät mit Anlage, Besitzer und Zustand, Problemfälle zuerst (so sortiert
 * die API). **Nur lesen:** Neustart, Update und Freigabe bleiben beim Besitzer,
 * und einen Link auf die Gerätedetailseite gibt es nicht — die gehört dem Konto
 * des Besitzers, und ein Admin hat dort kein Recht.
 *
 * Der Zustand kommt fertig vom Server, mit derselben Rechnung wie auf der
 * Geräteseite des Besitzers; Wörter und Töne sind dieselben
 * (`REACHABILITY_LABELS`, `REACHABILITY_TONES`). Gerechnet wird hier nichts.
 */
import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Alert, Badge, Card, Input, Select, breakpoints, useNarrow } from "@hausmeter/ui";
import type { ApiComponents } from "@hausmeter/sdk";
import { api } from "../lib/client.js";
import { REACHABILITY_LABELS, REACHABILITY_TONES } from "../lib/device-labels.js";
import { deviceTypeLabel, productName } from "../lib/device-type.js";
import { displayName, formatTimeAgo } from "../lib/format.js";
import { mutedText } from "../lib/list-styles.js";
import { navigate, type Route } from "../lib/router.js";

type AdminDevice = ApiComponents["schemas"]["AdminDevice"];
type Counts = ApiComponents["schemas"]["AdminDeviceList"]["counts"];
type State = keyof Omit<Counts, "total">;
type FleetRoute = Extract<Route, { kind: "fleet" }>;

/** So lange nach dem letzten Tastendruck wird gesucht — jede Suche liest alle Geräte. */
const SEARCH_DELAY_MS = 300;

const STALLED_LABEL = "keine neuen Werte";

/** Die Kacheln in der Reihenfolge der Frage: läuft es, und wenn nicht, wie schlimm. */
const TILES: readonly { readonly state: State; readonly label: string; readonly tone: "success" | "warning" | "danger" | "neutral" }[] = [
  { state: "reachable", label: REACHABILITY_LABELS.reachable, tone: REACHABILITY_TONES.reachable },
  { state: "readings_stalled", label: STALLED_LABEL, tone: "warning" },
  { state: "overdue", label: REACHABILITY_LABELS.overdue, tone: REACHABILITY_TONES.overdue },
  { state: "unreachable", label: REACHABILITY_LABELS.unreachable, tone: REACHABILITY_TONES.unreachable },
  { state: "never_seen", label: REACHABILITY_LABELS.never_seen, tone: REACHABILITY_TONES.never_seen },
];

export function FleetScreen({ route }: { readonly route: FleetRoute }) {
  const narrow = useNarrow(breakpoints.shell);
  /* Zustand und Typ stehen in der Adresse; die Suche ist eine Eingabe und keine Ansicht. */
  const { state, deviceType } = route;
  const set = (change: Partial<Pick<FleetRoute, "state" | "deviceType">>): void => navigate({ ...route, ...change });
  const [typed, setTyped] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setSearch(typed.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [typed]);

  const list = useQuery({
    queryKey: ["admin-devices", search, deviceType, state],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/devices", {
        params: {
          query: {
            ...(search ? { search } : {}),
            ...(deviceType === null ? {} : { deviceType }),
            ...(state === null ? {} : { state }),
          },
        },
      });
      if (error || !data) throw new Error("Die Geräte ließen sich nicht laden.");
      return data;
    },
    // Eine Zustandsanzeige, die beim Öffnen stehen bleibt, liefe nie in ihre Frist.
    refetchInterval: 60_000,
    placeholderData: (previous) => previous,
  });
  const counts = list.data?.counts;
  /* Ein gewählter Typ, den es unter der Suche nicht gibt, stünde unsichtbar als
     Filter da — die Auswahl zeigte „Alle Typen". Dann gilt er nicht mehr. */
  const offered = list.isPlaceholderData ? undefined : list.data?.deviceTypes;
  useEffect(() => {
    if (deviceType !== null && offered !== undefined && !offered.includes(deviceType)) {
      navigate({ ...route, deviceType: null }, { replace: true });
    }
  }, [deviceType, offered, route]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-4)" }}>
      <div
        role="group"
        aria-label="Zustand"
        style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 150px), 1fr))" }}
      >
        <Tile on={state === null} count={counts?.total} onClick={() => set({ state: null })} label="alle Geräte" />
        {TILES.map((tile) => (
          <Tile
            key={tile.state}
            on={state === tile.state}
            count={counts?.[tile.state]}
            onClick={() => set({ state: state === tile.state ? null : tile.state })}
            label={<Badge tone={tile.tone}>{tile.label}</Badge>}
          />
        ))}
      </div>

      <Card
        title="Geräte"
        subtitle="Wem jedes Gerät gehört und ob es sich meldet. Werte und Einstellungen der Geräte sind hier nicht einsehbar."
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--sp-3)", alignItems: "flex-end" }}>
          <div style={{ flex: "2 1 260px" }}>
            <Input
              label="Suche"
              value={typed}
              placeholder="Seriennummer, Gerätename oder E-Mail-Adresse des Besitzers"
              onChange={(event) => setTyped(event.target.value)}
            />
          </div>
          <div style={{ flex: "1 1 200px" }}>
            <Select
              label="Gerätetyp"
              value={deviceType ?? ""}
              onChange={(event) => set({ deviceType: event.target.value === "" ? null : event.target.value })}
              options={[
                { value: "", label: "Alle Typen" },
                ...(list.data?.deviceTypes ?? []).map((type) => ({ value: type, label: deviceTypeLabel(type) })),
              ]}
            />
          </div>
        </div>

        {list.isPending && <p style={{ ...mutedText, marginTop: "var(--sp-3)" }}>Wird geladen …</p>}
        {list.isError && (
          <div style={{ marginTop: "var(--sp-3)" }}>
            <Alert>{list.error.message}</Alert>
          </div>
        )}
        {list.data?.items.length === 0 && (
          <p style={{ ...mutedText, marginTop: "var(--sp-3)" }}>Keine Geräte mit dieser Auswahl.</p>
        )}
        {list.data !== undefined && list.data.items.length > 0 && (
          <FleetTable items={list.data.items} narrow={narrow} />
        )}
        {list.data?.truncated === true && (
          <p style={{ ...mutedText, marginTop: "var(--sp-3)" }}>
            Es passen mehr Geräte, als hier stehen — grenz die Suche ein.
          </p>
        )}
      </Card>
    </div>
  );
}

const COLUMNS = "minmax(0,1.4fr) minmax(0,1.2fr) 150px 130px 130px";

function FleetTable({ items, narrow }: { readonly items: readonly AdminDevice[]; readonly narrow: boolean }) {
  return (
    <div
      style={{
        marginTop: "var(--sp-3)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-lg)",
        overflow: "hidden",
      }}
    >
      {!narrow && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: COLUMNS,
            gap: 16,
            padding: "10px 18px",
            fontSize: 11.5,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "var(--text-muted)",
            borderBottom: "1px solid var(--border-subtle)",
            background: "var(--surface-inset)",
          }}
        >
          <span>Gerät</span>
          <span>Anlage und Besitzer</span>
          <span>Zustand</span>
          <span>Letzte Meldung</span>
          <span>Letzte Werte</span>
        </div>
      )}
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {items.map((device) => (
          <FleetRow key={device.id} device={device} narrow={narrow} />
        ))}
      </ul>
    </div>
  );
}

function FleetRow({ device, narrow }: { readonly device: AdminDevice; readonly narrow: boolean }) {
  const owner = device.owner === null ? "ohne Besitzer" : displayName(device.owner);
  const facts = [
    productName(device),
    device.serialNumber,
    device.firmwareVersion === null ? null : `Firmware ${device.firmwareVersion}`,
    device.gatewayDeviceId === null ? null : "hinter einem Gateway",
  ].filter((part): part is string => part !== null);

  const name = (
    <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-strong)", overflowWrap: "anywhere" }}>{device.name}</span>
      <span style={{ ...mutedText, fontSize: 12, overflowWrap: "anywhere" }}>{facts.join(" · ")}</span>
    </span>
  );
  const place = (
    <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      <span style={{ fontSize: 13, color: "var(--text-body)", overflowWrap: "anywhere" }}>{device.site.name}</span>
      <span style={{ ...mutedText, fontSize: 12, overflowWrap: "anywhere" }} title={device.owner?.email}>
        {owner}
      </span>
    </span>
  );
  const status = (
    <span style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      <Badge tone={REACHABILITY_TONES[device.reachability]}>{REACHABILITY_LABELS[device.reachability]}</Badge>
      {device.readingsStalled && <Badge tone="warning">{STALLED_LABEL}</Badge>}
    </span>
  );
  const seen = device.lastSeenAt === null ? "nie" : formatTimeAgo(device.lastSeenAt);
  const values = device.lastReadingAt === null ? "noch keine" : formatTimeAgo(device.lastReadingAt);

  if (narrow) {
    return (
      <li style={{ display: "flex", flexDirection: "column", gap: 6, padding: "12px 14px", borderTop: "1px solid var(--border-subtle)" }}>
        {name}
        {place}
        {status}
        <span style={{ ...mutedText, fontSize: 12 }}>
          Letzte Meldung {seen} · Letzte Werte {values}
        </span>
      </li>
    );
  }
  return (
    <li
      style={{
        display: "grid",
        gridTemplateColumns: COLUMNS,
        gap: 16,
        alignItems: "center",
        padding: "12px 18px",
        borderTop: "1px solid var(--border-subtle)",
      }}
    >
      {name}
      {place}
      {status}
      <span style={{ fontSize: 13, color: "var(--text-body)" }}>{seen}</span>
      <span style={{ fontSize: 13, color: "var(--text-body)" }}>{values}</span>
    </li>
  );
}

function Tile({
  on,
  count,
  onClick,
  label,
}: {
  readonly on: boolean;
  readonly count: number | undefined;
  readonly onClick: () => void;
  readonly label: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 8,
        padding: "14px 16px",
        borderRadius: "var(--radius-lg)",
        cursor: "pointer",
        textAlign: "left",
        fontFamily: "var(--font-body)",
        fontSize: 12.5,
        fontWeight: 600,
        color: "var(--text-body)",
        border: `1px solid ${on ? "var(--hm-green-500)" : "var(--border-subtle)"}`,
        background: on ? "var(--hm-green-50)" : "var(--surface-card)",
      }}
    >
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 26, fontWeight: 600, color: "var(--text-strong)", lineHeight: 1 }}>
        {count ?? "–"}
      </span>
      {label}
    </button>
  );
}
